import { useCallback, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/macro';
import { get } from 'lodash';
import { useDataService } from '../../../../services/ApiDataService';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import useActioningContext from '../../../../utilities/ActioningProviderUtilities';
import InfrastructureTable from '../BaseInfrastructureTable/index';
import { buildBulkActionPayload, useSelectionState } from '../InfrastructureActionHelper';
import { DEVICE_INTERFACE_COLUMNS } from '../interfaces/InterfaceConstants';
import { DEVICE_INTERFACE_DATA_SOURCE, INTERFACE_DELETED_FALSE_SOURCE } from '../InfrastructureConstants';
import { INTERFACE_PRIORITY_LOW_URL } from './DeviceConstants';
import useUiToasts, { createToastConfig, INFO } from '../../../../stores/ToastStore';
import { buildActionParams } from '../hooks/UseInfrastructureListActions';
import InterfaceActionMenusBox from '../interfaces/InterfaceActionMenusBox';

const EMPTY_STATE_MESSAGE = 'No interfaces available for this device';
const TABLE_ID = 'DeviceInterface';
const VISIBLE_ROWS = 10;

/**
 * Interface table with multi-select and action menus for Polling, Priority, and Speed.
 * Uses InfrastructureTable compound components pattern with selection enabled.
 * Fetches interface data from /query/admin/interfaces and supports bulk actions.
 * @returns {JSX.Element} InfrastructureTable with selection and action menu support
 */
const DeviceInterfaceTable = () => {
    const [, toastActions] = useUiToasts();
    const dataService = useDataService();
    const { id: deviceId } = useRecordContext();
    const { isActioning } = useActioningContext();
    const actionPostFetchRef = useRef(null);

    // Selection state management using shared hook
    const { selectionRef, isSelectionDisabled, handleSelectionChange, clearSelection } = useSelectionState();
    const [tableState, setTableState] = useState(undefined);

    // Bulk action handler invoked by InterfaceActionMenusBox
    const handleInterfaceAction = useCallback(
        async (actionUrl, extraPayload = {}) => {
            const body = buildBulkActionPayload(selectionRef.current, extraPayload);
            const queryParams = { query: JSON.stringify({ device: deviceId, ...INTERFACE_DELETED_FALSE_SOURCE }) };
            const params = buildActionParams({}, tableState, DEVICE_INTERFACE_DATA_SOURCE.params, queryParams);
            const response = await dataService.post(actionUrl, body, params);
            clearSelection();

            if (INTERFACE_PRIORITY_LOW_URL === actionUrl) {
                const wanCount = get(response, 'wanCount', -1);
                if (wanCount > 0) {
                    toastActions.createToast(
                        createToastConfig(
                            INFO,
                            'WAN interfaces are always High priority - one or more were not set to Low'
                        )
                    );
                }
            }

            actionPostFetchRef.current?.actionPostFetch();
        },
        [selectionRef, deviceId, tableState, dataService, clearSelection, toastActions]
    );

    const actionMenus = useMemo(
        () => (
            <InterfaceActionMenusBox disabled={isSelectionDisabled || isActioning} onAction={handleInterfaceAction} />
        ),
        [isSelectionDisabled, isActioning, handleInterfaceAction]
    );

    const fetchParams = useMemo(
        () => ({
            query: JSON.stringify({ device: deviceId, ...INTERFACE_DELETED_FALSE_SOURCE }),
            ...DEVICE_INTERFACE_DATA_SOURCE.params,
        }),
        [deviceId]
    );

    return (
        <InfrastructureTable
            title={<Trans>Interfaces</Trans>}
            tableId={TABLE_ID}
            emptyStateMessage={EMPTY_STATE_MESSAGE}
            fetchUrl={DEVICE_INTERFACE_DATA_SOURCE.url}
            fetchParams={fetchParams}
            columns={DEVICE_INTERFACE_COLUMNS}
            actionMenus={actionMenus}
            onSelectionChange={handleSelectionChange}
            visibleRows={VISIBLE_ROWS}
            dataArraySearchFields={DEVICE_INTERFACE_DATA_SOURCE.params.searchFields}
            apiRef={actionPostFetchRef}
        >
            <InfrastructureTable.Content setTableState={setTableState} enableSelection />
        </InfrastructureTable>
    );
};

export default DeviceInterfaceTable;
